import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ApiService } from '../../core/services/api.service';

@Component({
  selector: 'app-resource-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './resource-list.component.html',
  styleUrl: './resource-list.component.scss'
})
export class ResourceListComponent implements OnInit {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) apiPath!: string;
  @Input() subtitle = '';
  @Input() usePaged = true;

  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly rows = signal<Record<string, unknown>[]>([]);
  readonly totalCount = signal(0);
  readonly columns = signal<string[]>([]);

  searchText = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    const params = {
      page: 1,
      pageSize: 50,
      search: this.searchText.trim() || undefined
    };

    const request$ = this.usePaged
      ? this.api.getPaged<Record<string, unknown>>(this.apiPath, params)
      : this.api.get<unknown>(this.apiPath, params);

    request$.subscribe({
      next: (result) => {
        let items: Record<string, unknown>[] = [];
        let total = 0;

        if (this.usePaged && result && typeof result === 'object' && 'items' in result) {
          const paged = result as { items: Record<string, unknown>[]; totalCount: number };
          items = paged.items ?? [];
          total = paged.totalCount ?? items.length;
        } else if (Array.isArray(result)) {
          items = result as Record<string, unknown>[];
          total = items.length;
        } else if (result && typeof result === 'object') {
          const obj = result as Record<string, unknown>;
          if (Array.isArray(obj['items'])) {
            items = obj['items'] as Record<string, unknown>[];
            total = Number(obj['totalCount'] ?? items.length);
          } else {
            items = [obj];
            total = 1;
          }
        }

        this.rows.set(items);
        this.totalCount.set(total);
        this.columns.set(this.inferColumns(items));
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.rows.set([]);
        this.columns.set([]);
        this.error.set(
          err?.error?.message || err?.message || `Failed to load ${this.title}.`
        );
      }
    });
  }

  cellValue(row: Record<string, unknown>, column: string): string {
    const value = row[column];
    if (value == null) return '—';
    if (typeof value === 'object') {
      const nested = value as Record<string, unknown>;
      if (nested['displayName']) return String(nested['displayName']);
      if (nested['name']) return String(nested['name']);
      if (nested['email']) return String(nested['email']);
      return JSON.stringify(value);
    }
    return String(value);
  }

  private inferColumns(items: Record<string, unknown>[]): string[] {
    if (!items.length) return [];
    const preferred = [
      'name',
      'title',
      'email',
      'status',
      'jobTitle',
      'employeeNumber',
      'departmentName',
      'createdAt',
      'updatedAt',
      'id'
    ];
    const keys = Object.keys(items[0]).filter((k) => typeof items[0][k] !== 'undefined');
    const ordered = [
      ...preferred.filter((k) => keys.includes(k)),
      ...keys.filter((k) => !preferred.includes(k))
    ];
    return ordered.slice(0, 6);
  }
}
